/**
 * ConversationThreadRail — the conversation list beside an open conversation, built from the NextAdmin demo `/chats`
 * (left column: one row per chat with its name, a line of what it is about and the time). Opening a row navigates to
 * that conversation; the open one is highlighted.
 */
import { useQuery } from '@tanstack/react-query';
import { MessagesSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/Button.js';
import { Callout } from '../../../components/ui/Callout.js';
import { Card } from '../../../components/ui/Card.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { StatusDot, type StatusDotVariant } from '../../../components/ui/StatusDot.js';
import { listConversationsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { cn } from '../../../utils/cn.js';
import { relativeTime } from '../../../utils/relative-time.js';
import { STATUS_LABEL, type ConversationStatus, summarySnippet } from '../conversations-list/conversation-labels.js';

const STATUS_DOT: Record<ConversationStatus, StatusDotVariant> = {
  active: 'info',
  idle: 'neutral',
  completed: 'success',
  errored: 'danger',
};

interface ConversationThreadRailProps {
  activeConversationId: string;
}

export function ConversationThreadRail({ activeConversationId }: ConversationThreadRailProps) {
  const { data, isLoading, isError, refetch } = useQuery(
    listConversationsOptions({ query: { limit: 20, sort_by: 'lastActivityAt', sort_order: 'desc' } }),
  );
  const conversations = data?.data ?? [];

  return (
    <Card title="Conversations" className="h-full overflow-y-auto">
      {isLoading && (
        <div className="space-y-4" aria-label="Loading conversations">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="space-y-1.5">
              <Skeleton height={13} width="70%" />
              <Skeleton height={10} width="45%" />
            </div>
          ))}
        </div>
      )}
      {isError && (
        <Callout
          tone="danger"
          title="Could not load conversations"
          actions={
            <Button size="sm" variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      )}
      {!isLoading && !isError && conversations.length === 0 && (
        <EmptyState icon={MessagesSquare} title="No conversations yet" description="Start one from the list." />
      )}
      <ul className="-mx-2 flex flex-col gap-0.5">
        {conversations.map((conversation) => {
          const active = String(conversation.id) === activeConversationId;
          return (
            <li key={conversation.id}>
              <Link
                to={`/conversations/${conversation.id}`}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-surface-hover',
                  active && 'bg-accent-muted',
                )}
              >
                <StatusDot
                  variant={STATUS_DOT[conversation.status]}
                  title={STATUS_LABEL[conversation.status]}
                  className="mt-1.5 shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn('block truncate text-sm font-medium', active ? 'text-accent-text' : 'text-heading')}
                  >
                    {conversation.topic}
                  </span>
                  <span className="block truncate text-xs text-muted">
                    {summarySnippet(conversation.structuredSummary) ?? STATUS_LABEL[conversation.status]}
                  </span>
                </span>
                <time dateTime={conversation.lastActivityAt} className="shrink-0 text-xs text-faint">
                  {relativeTime(conversation.lastActivityAt)}
                </time>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
